import { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { FaReceipt } from 'react-icons/fa';
import type { Attachment, DropoffsFieldSet } from '@rogoag/airtable/crm';
import { LocalAtRecord } from '../../db';

/** Dropoffs table column holding the operator-facing dropoff instructions. */
export const DROPOFF_NOTES_FIELD = 'Drop Off Notes - Ops Mgr';
/** Dropoffs table column holding pictures of where the boxes are dropped off. */
export const DROPOFF_BOX_LOCATIONS_FIELD = 'Drop Off Box Locations';

export function getDropoffNotes(dropoffRecord: LocalAtRecord<DropoffsFieldSet> | undefined): string {
  return (dropoffRecord?.get(DROPOFF_NOTES_FIELD) || '').trim();
}

/**
 * Raw Airtable attachment URL of the first "Drop Off Box Locations" picture.
 * The column also holds documents (routes, PDFs), so only image attachments
 * count as pictures. Only the URL is read (no download) - these are signed
 * URLs, so the picture requires the device to be online.
 */
export function getBoxLocationPictureUrl(
  dropoffRecord: LocalAtRecord<DropoffsFieldSet> | undefined,
): string | undefined {
  // `.get()` is unsupported for attachment fields, so read the raw field value
  const attachments = dropoffRecord?.fields?.[DROPOFF_BOX_LOCATIONS_FIELD] || [];

  return attachments.filter(isPicture).find((attachment) => attachment.url)?.url || undefined;
}

const PICTURE_FILE_NAME = /\.(png|jpe?g|gif|webp|bmp|heic|heif|avif)$/i;

function isPicture(attachment: Partial<Attachment>): boolean {
  if (attachment.type) {
    return attachment.type.startsWith('image/');
  }

  // older records can be missing the mime type - fall back to the file name
  return PICTURE_FILE_NAME.test(attachment.filename || '');
}

type DropoffNotesButtonProps = {
  dropoffName: string;
  dropoffRecord: LocalAtRecord<DropoffsFieldSet> | undefined;
};

const DropoffNotesButton = ({ dropoffName, dropoffRecord }: DropoffNotesButtonProps) => {
  const [dialogVisible, setDialogVisible] = useState(false);
  const notes = getDropoffNotes(dropoffRecord);
  const pictureUrl = getBoxLocationPictureUrl(dropoffRecord);
  const hasNotesOrPicture = Boolean(notes || pictureUrl);

  return (
    <>
      <Tooltip title={hasNotesOrPicture ? 'Dropoff Notes' : 'No Dropoff Notes'} placement="top">
        <span>
          <IconButton
            aria-label="Dropoff Notes"
            disabled={!dropoffRecord || !hasNotesOrPicture}
            color="secondary"
            size="large"
            onClick={async () => {
              setDialogVisible(true);
            }}
          >
            <FaReceipt fontSize={32} />
          </IconButton>
        </span>
      </Tooltip>

      <Dialog open={dialogVisible} onClose={() => setDialogVisible(false)} maxWidth="md" fullWidth>
        <DialogTitle align="center">{`Dropoff Notes - ${dropoffName}`}</DialogTitle>
        <DialogContent>
          {notes ? (
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', marginBottom: pictureUrl ? 2 : 0 }}>
              {notes}
            </Typography>
          ) : (
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ marginBottom: pictureUrl ? 2 : 0 }}
            >{`No notes available for ${dropoffName}.`}</Typography>
          )}

          {pictureUrl ? (
            <Box sx={{ textAlign: 'center' }}>
              <Box
                component="img"
                src={pictureUrl}
                alt={`Drop off box location for ${dropoffName}`}
                sx={{ maxWidth: '100%', maxHeight: '60vh', objectFit: 'contain' }}
              />
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setDialogVisible(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DropoffNotesButton;
